import type { PlayerController } from '../player/controller';
import type { FollowCamera } from '../scene/camera';
import { haptic, getPrefs, onPrefsChange, type Prefs } from '../settings/prefs';
import { getTouchLabels } from './touch_labels';

export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
  );
}

export type TouchUIHandle = {
  el: HTMLDivElement;
  destroy: () => void;
  rebuild: () => void;
  // Layout editor hooks. While `editMode` is true, the live game input
  // handlers on the joystick and buttons no-op so the editor can use
  // their elements as drag targets.
  setEditMode: (on: boolean) => void;
  getJoystickEl: () => HTMLDivElement;
  getButtonsEl: () => HTMLDivElement;
};

// Single live touch UI handle. Layout editor reads it to operate on
// the same joystick + button elements the player is using right now.
let activeHandle: TouchUIHandle | null = null;
export function getActiveTouchUI(): TouchUIHandle | null { return activeHandle; }

// Mounts an on-screen joystick (left thumb), drag-look (the half of the
// screen opposite the joystick), and big USE/E/HOOD/TAG buttons. Wires
// straight into PlayerController (externalAxis) and FollowCamera
// yaw/pitch.
//
// All sizes + positions are read from the player's pref store so a
// settings change rebuilds the layout live.
export function mountTouchUI(
  parent: HTMLElement,
  controller: PlayerController,
  camera: FollowCamera,
): TouchUIHandle {
  let root: HTMLDivElement = document.createElement('div');
  let joystick: HTMLDivElement = document.createElement('div');
  let buttons: HTMLDivElement = document.createElement('div');
  let editMode = false;
  // Per-build rAF loop cancellers (sub-label sync). build() flushes
  // these before re-allocating so we don't leak a runaway loop after
  // a settings-driven rebuild.
  const labelLoopCancellers: Array<() => void> = [];

  const TOUCH_KEYS: ReadonlySet<keyof Prefs> = new Set<keyof Prefs>([
    'touchJoystickSize',
    'touchButtonSize',
    'touchJoystickAnchor',
    'touchButtonsAnchor',
    'touchJoystickOffsetX',
    'touchJoystickOffsetY',
    'touchButtonsOffsetX',
    'touchButtonsOffsetY',
  ]);

  function build(): void {
    while (labelLoopCancellers.length > 0) labelLoopCancellers.pop()!();
    root.remove();

    const prefs = getPrefs();

    root = document.createElement('div');
    root.className = 'ui interactive';
    root.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 50;
      user-select: none; touch-action: none;
    `;
    parent.appendChild(root);

    // ---- Joystick ----
    const ja = prefs.touchJoystickAnchor;
    const jSize = prefs.touchJoystickSize;
    const knobSize = Math.round(jSize * 0.43);
    const stickRadius = Math.round(jSize * 0.385);

    joystick = document.createElement('div');
    joystick.style.cssText = `
      position: fixed;
      ${ja}: max(${prefs.touchJoystickOffsetX}px, env(safe-area-inset-${ja}));
      bottom: max(${prefs.touchJoystickOffsetY}px, env(safe-area-inset-bottom));
      width: ${jSize}px; height: ${jSize}px;
      border-radius: 50%;
      background: rgba(255,255,255,.08);
      border: 2px solid rgba(255,255,255,.18);
      pointer-events: auto;
      touch-action: none;
    `;
    const knob = document.createElement('div');
    knob.style.cssText = `
      position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
      width: ${knobSize}px; height: ${knobSize}px; border-radius: 50%;
      background: rgba(255,255,255,.35);
      pointer-events: none;
    `;
    joystick.appendChild(knob);
    root.appendChild(joystick);

    let stickPointerId: number | null = null;
    let stickCenter = { x: 0, y: 0 };

    const updateStick = (x: number, y: number) => {
      const dx = x - stickCenter.x;
      const dy = y - stickCenter.y;
      const d = Math.hypot(dx, dy);
      const k = d > stickRadius ? stickRadius / d : 1;
      const cx = dx * k;
      const cy = dy * k;
      knob.style.transform = `translate(calc(-50% + ${cx}px), calc(-50% + ${cy}px))`;
      controller.externalAxis = {
        x: cx / stickRadius,
        y: -cy / stickRadius,
      };
    };

    const resetStick = () => {
      stickPointerId = null;
      knob.style.transform = 'translate(-50%, -50%)';
      controller.externalAxis = null;
    };

    joystick.addEventListener('pointerdown', (e) => {
      if (editMode) return;
      e.preventDefault();
      joystick.setPointerCapture(e.pointerId);
      stickPointerId = e.pointerId;
      const rect = joystick.getBoundingClientRect();
      stickCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      updateStick(e.clientX, e.clientY);
    });
    joystick.addEventListener('pointermove', (e) => {
      if (editMode) return;
      if (stickPointerId !== e.pointerId) return;
      updateStick(e.clientX, e.clientY);
    });
    const stickEnd = (e: PointerEvent) => {
      if (editMode) return;
      if (stickPointerId !== e.pointerId) return;
      resetStick();
    };
    joystick.addEventListener('pointerup', stickEnd);
    joystick.addEventListener('pointercancel', stickEnd);

    // ---- Action buttons ----
    // Mirror the desktop layout: each big button shows the desktop key
    // letter ("E", "Q", "R") on top + a small contextual sub-label
    // beneath in parens, e.g. "E (HOOD)" or "Q (ULT)". A separate "HOOD"
    // button used to live here — folded into E so the contract matches
    // the desktop key (which already does interact-or-hood fallback).
    const ba = prefs.touchButtonsAnchor;
    const bSize = prefs.touchButtonSize;

    buttons = document.createElement('div');
    buttons.style.cssText = `
      position: fixed;
      ${ba}: max(${prefs.touchButtonsOffsetX}px, env(safe-area-inset-${ba}));
      bottom: max(${prefs.touchButtonsOffsetY}px, env(safe-area-inset-bottom));
      display: grid; grid-template-columns: repeat(2, auto); gap: 12px;
      pointer-events: auto;
    `;
    const RING_W = 5; // ring thickness in px
    const makeKeyButton = (
      keyLetter: string,
      color: string,
      action: 'interact' | 'ult' | 'use1',
      size = bSize,
    ) => {
      // Frame holds the conic-gradient cooldown ring; the button sits
      // inside it at full opacity. Frame is `display: flex` so we can
      // toggle visibility via the `frame` element from the rAF poll.
      const frame = document.createElement('div');
      frame.style.cssText = `
        position: relative;
        width: ${size + RING_W * 2}px; height: ${size + RING_W * 2}px;
        border-radius: 50%;
        background: ${color};
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 6px 18px rgba(0,0,0,.45);
      `;
      const b = document.createElement('button');
      b.style.cssText = `
        position: relative;
        width: ${size}px; height: ${size}px; border-radius: 50%;
        background: ${color}; color: #0b0d12; border: 0;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 0;
        font: 900 ${Math.round(size * 0.34)}px system-ui, sans-serif; letter-spacing: .5px;
        touch-action: none;
        padding: 0;
        z-index: 1;
      `;
      const main = document.createElement('span');
      main.textContent = keyLetter;
      main.style.cssText = `line-height: 1;`;
      const sub = document.createElement('span');
      sub.style.cssText = `
        font: 800 ${Math.round(size * 0.13)}px system-ui, sans-serif;
        letter-spacing: .8px;
        opacity: .82;
        max-width: 90%;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        margin-top: 2px;
      `;
      b.append(main, sub);
      frame.appendChild(b);
      b.addEventListener('pointerdown', (e) => {
        if (editMode) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('smp:action', { detail: action }));
        b.style.transform = 'scale(.92)';
        haptic(15);
      });
      b.addEventListener('pointerup', () => { b.style.transform = ''; });
      b.addEventListener('pointercancel', () => { b.style.transform = ''; });
      // Ring updater. `charge` is 0..1 (visible portion), or null = no
      // cooldown logic (E and R buttons → fully colored ring).
      const setCharge = (charge: number | null) => {
        if (charge == null) {
          // Solid ring matches the button face — looks like a normal
          // button with a slightly raised border.
          frame.style.background = color;
          b.style.opacity = '1';
          return;
        }
        const deg = Math.max(0, Math.min(1, charge)) * 360;
        // Filled portion = button color, empty portion = dim slate so
        // the contrast reads at a glance. Conic starts at -90° (12
        // o'clock) so the fill grows clockwise like a stopwatch.
        frame.style.background =
          `conic-gradient(from -90deg, ${color} 0deg ${deg}deg, rgba(15,23,42,.78) ${deg}deg 360deg)`;
        // Dim the button face while not ready so the visual reads
        // "ULT charging" at a glance, even if the player isn't
        // tracking the ring fill exactly.
        b.style.opacity = charge >= 1 ? '1' : '.62';
      };
      return { btn: b, frame, sub, setCharge };
    };
    // E — interact + hood fallback (matches desktop E key contract).
    const eBtn = makeKeyButton('E', '#fde047', 'interact');
    // Q — ultimate. Stays mounted always: ring shows the charge.
    const qBtn = makeKeyButton('Q', '#22d3ee', 'ult');
    // R — mythic use. Hidden until a mythic is owned.
    const rBtn = makeKeyButton('R', '#c4b5fd', 'use1');
    rBtn.frame.style.display = 'none';
    // Sprint toggle button — flips controller.sprintToggle on each tap.
    // Background tints when ON so the player has a glance-able state.
    const sprintBtn = document.createElement('button');
    const SPRINT_OFF_BG = 'rgba(148,163,184,.85)';
    const SPRINT_ON_BG = 'linear-gradient(135deg,#22d3ee,#fde047)';
    const renderSprintBtn = () => {
      sprintBtn.style.background = controller.sprintToggle ? SPRINT_ON_BG : SPRINT_OFF_BG;
      sprintBtn.textContent = controller.sprintToggle ? '⚡ ON' : 'SPRINT';
    };
    sprintBtn.style.cssText = `
      width: ${bSize}px; height: ${bSize}px; border-radius: 50%;
      color: #0b0d12; border: 0;
      font: 800 12px system-ui, sans-serif; letter-spacing: .5px;
      box-shadow: 0 6px 18px rgba(0,0,0,.45);
      touch-action: none;
    `;
    renderSprintBtn();
    sprintBtn.addEventListener('pointerdown', (e) => {
      if (editMode) return;
      e.preventDefault();
      controller.setSprintToggle(!controller.sprintToggle);
      renderSprintBtn();
      sprintBtn.style.transform = 'scale(.92)';
      haptic(15);
    });
    sprintBtn.addEventListener('pointerup', () => { sprintBtn.style.transform = ''; });
    sprintBtn.addEventListener('pointercancel', () => { sprintBtn.style.transform = ''; });
    buttons.append(eBtn.frame, qBtn.frame, rBtn.frame, sprintBtn);
    root.appendChild(buttons);

    // Sub-label + cooldown ring sync — poll the shared registry on rAF.
    // String / number compares short-circuit when nothing changed so
    // DOM writes only happen on real state transitions. Tear down on
    // rebuild via the rafCancelled flag.
    let rafCancelled = false;
    let lastE = '\0', lastQ = '\0', lastR = '\0', lastQCharge = -2;
    const syncLabels = () => {
      if (rafCancelled) return;
      const L = getTouchLabels();
      const eText = L.e ? `(${L.e})` : '';
      if (eText !== lastE) { lastE = eText; eBtn.sub.textContent = eText; }
      if ((L.q ?? '\0') !== lastQ) {
        lastQ = L.q ?? '\0';
        qBtn.sub.textContent = L.q ? `(${L.q})` : '';
      }
      // Q stays mounted always; cooldown ring reflects qCharge. Round
      // to 2 decimals so micro-deltas (60fps × continuous fill) don't
      // thrash the conic-gradient string.
      const qcRaw = L.qCharge;
      const qc = qcRaw == null ? -1 : Math.round(qcRaw * 100) / 100;
      if (qc !== lastQCharge) {
        lastQCharge = qc;
        qBtn.setCharge(qcRaw);
      }
      if ((L.r ?? '\0') !== lastR) {
        lastR = L.r ?? '\0';
        rBtn.frame.style.display = L.r ? 'flex' : 'none';
        rBtn.sub.textContent = L.r ? `(${L.r})` : '';
      }
      requestAnimationFrame(syncLabels);
    };
    requestAnimationFrame(syncLabels);
    labelLoopCancellers.push(() => { rafCancelled = true; });

    // ---- Drag-to-look ----
    // Lives on the half of the screen opposite the joystick so the
    // player's joystick thumb isn't competing with look-look events.
    const lookSide: 'left' | 'right' = ja === 'left' ? 'right' : 'left';
    const lookOverlay = document.createElement('div');
    lookOverlay.style.cssText = `
      position: fixed; top: 0; ${lookSide}: 0; width: 50%; height: 100%;
      pointer-events: auto; touch-action: none;
    `;
    root.appendChild(lookOverlay);

    let lookPointerId: number | null = null;
    let lastLook = { x: 0, y: 0 };
    lookOverlay.addEventListener('pointerdown', (e) => {
      if (editMode) return;
      if (lookPointerId !== null) return;
      lookOverlay.setPointerCapture(e.pointerId);
      lookPointerId = e.pointerId;
      lastLook = { x: e.clientX, y: e.clientY };
    });
    lookOverlay.addEventListener('pointermove', (e) => {
      if (editMode) return;
      if (lookPointerId !== e.pointerId) return;
      const dx = e.clientX - lastLook.x;
      const dy = e.clientY - lastLook.y;
      lastLook = { x: e.clientX, y: e.clientY };
      // Look sensitivity is read live so a pref change applies on the
      // next gesture without a rebuild.
      const sens = getPrefs().touchLookSens;
      camera.yaw -= dx * 0.005 * sens;
      camera.pitch -= dy * 0.0035 * sens;
    });
    const lookEnd = (e: PointerEvent) => {
      if (editMode) return;
      if (lookPointerId !== e.pointerId) return;
      lookPointerId = null;
    };
    lookOverlay.addEventListener('pointerup', lookEnd);
    lookOverlay.addEventListener('pointercancel', lookEnd);
  }

  build();

  const unsub = onPrefsChange((changed) => {
    for (const k of changed) {
      if (TOUCH_KEYS.has(k)) {
        build();
        return;
      }
    }
  });

  const handle: TouchUIHandle = {
    get el() { return root; },
    destroy() {
      unsub();
      root.remove();
      if (activeHandle === handle) activeHandle = null;
    },
    rebuild: build,
    setEditMode(on: boolean) { editMode = on; },
    getJoystickEl() { return joystick; },
    getButtonsEl() { return buttons; },
  };
  activeHandle = handle;
  return handle;
}
